import React, { useEffect, useMemo, useRef, useState, memo } from 'react';
import Container from "@mui/material/Container";
import styles from './order-feedback.module.scss';
import withTitle from '../../../components/withTitle/withTitle';
import { useTranslation } from 'react-i18next';
import { Button, DialogActions, TextField, MenuItem } from "@mui/material";
import { useForm, useController, useWatch, Controller } from 'react-hook-form';
import { HTTPMethod, callApi } from '../../../services/HttpService/HttpService';
import { OrderFeedbackApi } from '../../../constants/ApiConstants';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import NoData from '../../../components/noData/noData';
import loader from "../../../assets/loader.gif";
import appSettings from "../../../configuration/settings.json";
import { feedbackInputType } from '../../../common/enums';
import RatingsV2 from "./ratings-v2";
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';

const StableTextArea = memo(function StableTextArea({ name, control, placeholder }) {
    const { field } = useController({ name, control, defaultValue: "" });
    return (
        <TextField
            inputRef={field.ref}
            name={field.name}
            value={field.value ?? ""}
            onChange={field.onChange}
            onBlur={field.onBlur}
            multiline
            fullWidth
            rows={4}
            placeholder={placeholder || ""}
            variant="outlined"
            size="small"
            className={styles.textField}
        />
    );
});

const YesNoField = memo(function YesNoField({ question, control, setValue, orderFeedbackQuestions }) {
    const answer = useWatch({ control, name: `question_${question.feedback_question_id}`, defaultValue: '' });
    const isNoSelected = answer === 'No';

    const dependentDropdown = useMemo(() => (
        orderFeedbackQuestions.find(
            q => q.dependent_feedback_question_id === question.feedback_question_id &&
                q.dependent_feedback_question_value === 'No' &&
                q.input_type === feedbackInputType.Dropdown
        ) || null
    ), [orderFeedbackQuestions, question.feedback_question_id]);

    return (
        <div>
            <div className={styles.choiceOption}>
                <Button
                    variant={answer === 'Yes' ? 'contained' : 'outlined'}
                    onClick={() => setValue(`question_${question.feedback_question_id}`, 'Yes')}
                    className={`${styles.yesNoButton} ${answer === 'Yes' ? styles.yesSelected : ''}`}
                >
                    Yes
                </Button>
                <Button
                    variant={answer === 'No' ? 'contained' : 'outlined'}
                    onClick={() => setValue(`question_${question.feedback_question_id}`, 'No')}
                    className={`${styles.yesNoButton} ${answer === 'No' ? styles.noSelected : ''}`}
                >
                    No
                </Button>
            </div>


            {isNoSelected && dependentDropdown && (
                <div style={{ marginTop: '1rem' }}>
                    <p className="fontWeight600">{dependentDropdown.input_label}</p>
                    <Controller
                        key={`dd-${dependentDropdown.feedback_question_id}`}
                        name={`question_${dependentDropdown.feedback_question_id}`}
                        control={control}
                        defaultValue={''}
                        render={({ field }) => (
                            <TextField
                                {...field}
                                select
                                fullWidth
                                variant="outlined"
                                size="small"
                                className={styles.textField}
                                SelectProps={{
                                    displayEmpty: true,
                                    renderValue: (selected) => selected ? selected : <span style={{ color: 'var(--color-lightgrey-100)' }}>Select an option</span>
                                }}
                            >
                                {(dependentDropdown.input_list_items || '').split(/[|;]/).filter(Boolean).map((opt, idx) => (
                                    <MenuItem key={idx} value={opt.trim()} sx={{ whiteSpace: 'normal', wordBreak: 'break-word', lineHeight: 1.4 }}>
                                        {opt.trim()}
                                    </MenuItem>
                                ))}
                            </TextField>
                        )}
                    />
                </div>
            )}
        </div>
    );
});

const OrderFeedbackV2 = () => {
    const { t } = useTranslation();
    const [orderFeedbackData, setOrderFeedbackData] = useState(null);
    const [formAlreadySubmitted, setFormAlreadySubmitted] = useState(false);
    const [isLoading, setLoading] = useState(true);
    const [feedbackGuid, setFeedbackGuid] = useState('');
    const [orderFeedbackQuestions, setOrderFeedbackQuestions] = useState([]);
    const [isSubmitted, setIsSubmitted] = useState(false);

    const [activeQuestionId, setActiveQuestionId] = useState(null);

    const { handleSubmit, control, getValues, setValue, watch } = useForm();
    const navigate = useNavigate();
    const location = useLocation();

    const isLoginUser = useSelector(state => state.authState.isLoggedIn);
    const orderId = location.state?.orderFeedbackId || JSON.parse(localStorage.getItem('orderId'));
    const [feedbackOrderId, setFeedbackOrderId] = useState(orderId || sessionStorage.getItem('feedbackOrderId'));
    const isV2Path = location.pathname.includes('/orders/order-feedback-v2');

    const q1Id = orderFeedbackQuestions[0]?.feedback_question_id;
    const q1Answer = watch(q1Id ? `question_${q1Id}` : undefined);
    const isFirstRenderRef = useRef(true);
    
    const debounce = (fn, delay = 400) => {
        let t;
        return Object.assign(
            (...args) => {
                clearTimeout(t);
                t = setTimeout(() => fn(...args), delay);
            },
            {
                flush: () => {
                    if (t) {
                        clearTimeout(t);
                        t = null;
                        fn();
                    }
                },
                cancel: () => {
                    clearTimeout(t);
                    t = null;
                }
            }
        );
    };
    
    const savePartialResponse = async (questionId, rawValue) => {
        if (!questionId) return false;

        const response =
            rawValue === null || rawValue === undefined
                ? null
                : rawValue.toString();
        const payload = {
            order_feedback_response_model: {
                feedback_question_id: questionId,
                response,
                is_completed: false,
                order_feedback_id: feedbackGuid || orderFeedbackData?.order_feedback_id,
                ref_order_id: orderFeedbackData?.order_id,
                user_id: isLoginUser ? orderFeedbackData?.user_id : null,
                survey_version: 2
            }
        };
        return await onSubmit(null, payload);
    };
    
    const debouncedSaveRef = useRef(
        debounce(async (questionId, value) => {
            await savePartialResponse(questionId, value);
        }, 500)
    );

    useEffect(() => { localStorage.setItem('drawerSelectedItem', 'Orders'); }, []);

    useEffect(() => {
        const queryParams = new URLSearchParams(location.search);
        const feedbackId = queryParams.get('Order-feedback-id');
        if (feedbackId) {
            setFeedbackGuid(feedbackId);
            localStorage.removeItem('orderId');
            sessionStorage.removeItem('feedbackOrderId');
        } else if (orderId) {
            setFeedbackOrderId(orderId);
            sessionStorage.setItem('feedbackOrderId', orderId);
        } else if (!feedbackGuid && !feedbackOrderId) {
            navigate('/error');
        }
    }, [location.search, orderId, feedbackGuid, feedbackOrderId, navigate]);

    useEffect(() => {
        if (feedbackGuid) {
            const url = OrderFeedbackApi.orderFeedbackPage.replace('{order_feedback_id}', feedbackGuid);
            const questionsUrl = OrderFeedbackApi.orderFeedbackPageQuestions.replace('{order_feedback_id}', feedbackGuid);
            getOrderFeedback(url, questionsUrl, true);
        }
    }, [feedbackGuid]);

    useEffect(() => {
        if (!feedbackGuid && feedbackOrderId) {
            const url = OrderFeedbackApi.orderFeedbackPageAuth.replace('{order_id}', feedbackOrderId);
            const questionsUrl = OrderFeedbackApi.orderFeedbackPageAuthQuestions.replace('{order_id}', feedbackOrderId);
            getOrderFeedback(url, questionsUrl);
        }
    }, [feedbackOrderId, feedbackGuid]);

    useEffect(() => {
        if (!isV2Path && isLoginUser && feedbackGuid) {
            navigate('/orders/feedback-order' + '?Order-feedback-id=' + feedbackGuid);
        }
    }, [isLoginUser, feedbackGuid, isV2Path]);

    const getOrderFeedback = async (url, questionsUrl, isAnonymousCall = false) => {
        setOrderFeedbackData(null);
        const orderFeedbackResponse = await callApi(HTTPMethod.Get, url, null, 'event-api-call', 1, isAnonymousCall);

        if (orderFeedbackResponse?.status_code === 200 && orderFeedbackResponse?.data && orderFeedbackResponse?.data?.is_link_expired !== true) {
            setOrderFeedbackData(orderFeedbackResponse?.data);
            setFormAlreadySubmitted(orderFeedbackResponse.data?.is_form_submitted);
            localStorage.setItem('orderId', orderFeedbackResponse.data?.order_id);

            if (!orderFeedbackResponse.data?.is_form_submitted) {
                const orderFeedbackResponseQuestions = await callApi(
                    HTTPMethod.Get,
                    questionsUrl,
                    null,
                    'event-api-call',
                    1,
                    isAnonymousCall
                );

                if (orderFeedbackResponseQuestions?.status_code === 200 && orderFeedbackResponseQuestions?.data) {
                    const qs = orderFeedbackResponseQuestions?.data?.feedback_question || [];
                    setOrderFeedbackQuestions(qs);
                    setLoading(false);
                    if (qs.length > 0) setActiveQuestionId(qs[0].feedback_question_id);
                } else {
                    setLoading(false);
                }
            } else {
                setLoading(false);
            }
         } else if (orderFeedbackResponse?.data?.is_link_expired === true) {
                navigate('/feedback-expired', { replace: true });
              } else {
             setLoading(false);
         }
    };
    const visibleQuestions = useMemo(() => {
        if (orderFeedbackQuestions.length === 0) return [];
        
        const isPickup = orderFeedbackQuestions.some(q => q.filter_order_type === 2);

        let vq = [orderFeedbackQuestions[0]];

        if (["1", "2", "3"].includes(q1Answer?.toString())) {
            if (isPickup) {
                vq = [
                    orderFeedbackQuestions.find(q => q.feedback_question_id === 24),
                    orderFeedbackQuestions.find(q => q.feedback_question_id === 35),
                    orderFeedbackQuestions.find(q => q.feedback_question_id === 33) 
                ].filter(Boolean);
            } else {
                vq = [
                    ...vq,
                    ...orderFeedbackQuestions.filter(q =>
                        q.feedback_question_id === 27 ||
                        (shouldShowQuestion(q) && q.input_type !== feedbackInputType.Dropdown)
                    )
                ];
            }
        } else if (["4", "5"].includes(q1Answer?.toString())) {
            vq = [
                ...vq,
                ...orderFeedbackQuestions.filter(q => q.feedback_question_id === 26)
            ];
        }
        
        const dedup = vq.filter(
            (q, idx, self) =>
                idx === self.findIndex(q2 => q2.feedback_question_id === q.feedback_question_id)
        );

        return dedup;
    }, [orderFeedbackQuestions, q1Answer]);

    useEffect(() => {
        if (visibleQuestions.length === 0) return;
        if (!activeQuestionId || !visibleQuestions.some(q => q.feedback_question_id === activeQuestionId)) {
            setActiveQuestionId(visibleQuestions[0].feedback_question_id);
        }
    }, [visibleQuestions, activeQuestionId]);

    const currentIndex = Math.max(0, visibleQuestions.findIndex(q => q.feedback_question_id === activeQuestionId));
    const activeQuestion = visibleQuestions[currentIndex] || null;

    function shouldShowQuestion(question) {
        if (!question?.dependent_feedback_question_id) return true;

        const dependentValue = question.dependent_feedback_question_value;
        const dependentAnswer = (getValues(`question_${question.dependent_feedback_question_id}`) ?? '').toString();
        if (dependentAnswer) return dependentValue.split('|').includes(dependentAnswer);

        const parent = orderFeedbackQuestions.find(q => q.feedback_question_id === question.dependent_feedback_question_id);
        if (parent?.response) return dependentValue.split('|').includes(parent.response.toString());
        return false;
    }

    const goToNext = () => {
        if (!activeQuestion) return;
        const nextIndex = Math.min(currentIndex + 1, visibleQuestions.length - 1);
        setActiveQuestionId(visibleQuestions[nextIndex].feedback_question_id);
    };
    
    const goToPrevious = () => {
        const prevIndex = Math.max(currentIndex - 1, 0);
        setActiveQuestionId(visibleQuestions[prevIndex].feedback_question_id);
    };

    const handleFinalSubmit = async () => {
        if (orderFeedbackQuestions?.length) {
            const textQs = orderFeedbackQuestions.filter(
                q => q.input_type === feedbackInputType.Text
            );
            for (const q of textQs) {
                const val = (getValues(`question_${q.feedback_question_id}`) ?? '').toString().trim();
                if (val) {
                    await savePartialResponse(q.feedback_question_id, val);
                }
            }
        }
        
        const ok = await onSubmit(null);
        if (ok) {
            setIsSubmitted(true);
            localStorage.removeItem('orderId');
            sessionStorage.removeItem('feedbackOrderId');
        }
    };

    const onSubmit = async (data, partialDataPayload) => {
        const basePayload = {
            order_feedback_response_model: {
                feedback_question_id: 0,
                response: null,
                is_completed: true,
                order_feedback_id: feedbackGuid || orderFeedbackData?.order_feedback_id,
                ref_order_id: orderFeedbackData?.order_id,
                user_id: isLoginUser ? orderFeedbackData?.user_id : null,
                survey_version: 2
            }
        };
        
        const feedbackPayload = partialDataPayload
            ? { order_feedback_response_model: { ...basePayload.order_feedback_response_model, ...partialDataPayload.order_feedback_response_model} }
            : basePayload;

        const url = !feedbackGuid
            ? OrderFeedbackApi.orderFeedbackPageAuth.replace('{order_id}', orderFeedbackData?.order_id)
            : OrderFeedbackApi.orderFeedbackPage.replace('{order_feedback_id}', feedbackGuid);

        const feedbackResponse = await callApi(
            HTTPMethod.Post,
            url,
            feedbackPayload,
            'event-order-feedback',
            !feedbackGuid ? undefined : 1,
            !feedbackGuid ? undefined : true
        );

        if (feedbackResponse?.status_code === 200) {
               return true;
        }
        return false;
    };

    const canGoNext = (() => {
        if (!activeQuestion) return false;

        const currentAnswer = getValues(`question_${activeQuestion.feedback_question_id}`);
        
        if (currentIndex === 0) {
            return !!currentAnswer;
        }
        
        if (
            currentAnswer === 'No' &&
            orderFeedbackQuestions.some(
                q =>
                    q.dependent_feedback_question_id === activeQuestion.feedback_question_id &&
                    q.input_type === feedbackInputType.Dropdown &&
                    !getValues(`question_${q.feedback_question_id}`)
            )
        ) {
            return false;
        }
        return true;
    })();

    useEffect(() => {
        if (!orderFeedbackQuestions || orderFeedbackQuestions.length === 0) return;

        const subscription = watch(async (_value, { name }) => {
            if (!name?.startsWith('question_')) return;
            if (isFirstRenderRef.current) return;

            const qid = Number(name.replace('question_', ''));
            if (!qid) return;
            
            const q = orderFeedbackQuestions.find(q => q.feedback_question_id === qid);
            const valueNow = getValues(name);

            if (!q) return;
            
            if (q.input_type !== feedbackInputType.Text) {
                await savePartialResponse(qid, valueNow);
            }

            const isYesNo = q.input_type === feedbackInputType.YesNo;
            if (isYesNo) {
                const dependents = orderFeedbackQuestions.filter(
                    d => d.dependent_feedback_question_id === qid
                );
                if (dependents.length) {
                    for (const dep of dependents) {
                        const showValues = (dep.dependent_feedback_question_value || '')
                            .split('|')
                            .filter(Boolean);
                        const shouldShow = showValues.includes((valueNow ?? '').toString());
                        if (!shouldShow) {
                            const depName = `question_${dep.feedback_question_id}`;
                            const depCurrent = getValues(depName);
                            if (depCurrent) {
                                setValue(depName, '', { shouldDirty: true, shouldTouch: true });
                                if (dep.input_type !== feedbackInputType.Text) {
                                    await savePartialResponse(dep.feedback_question_id, null);
                                }
                            }
                        }
                    }
                }
            }
        });
        
        isFirstRenderRef.current = false;

        return () => {
            subscription.unsubscribe?.();
        };
    }, [watch, orderFeedbackQuestions, getValues, isLoginUser, feedbackGuid, orderFeedbackData]);

    return (
        <>
            {isSubmitted ? (
                <div className={styles.thankYouCenter}>
                    <CheckCircleRoundedIcon className={styles.checkIcon} />
                    <h2 className={styles.thankYouTitle}>
                        {t('OrderFeedback.thankYouTitle')}
                    </h2>
                    <p className={styles.thankYouBody}>
                        {t('OrderFeedback.thankYouBody')}
                    </p>
                </div>
            ) : 
                formAlreadySubmitted ? (
                <div data-testid="OrderFeedbackAlreadySubmitted" style={{ marginTop: '20px' }}>
                    <NoData message={t("OrderFeedback.feedbackFormIsAlreadySubmitted")} />
                </div>
                ) : (
                <div className={styles.OrderFeedback} data-testid="OrderFeedback">
                    <Container className={`${styles.marginTop10} mainContainer`}>
                        {(orderFeedbackData && orderFeedbackQuestions?.length > 0) ? (
                            <div className={styles.marginTop20}>
                                <div className={styles.surveyV2Header}>
                                    <img src={appSettings.settings_CompanyLogo} alt="DMETrack" />
                                        <h2>{t('OrderFeedback.surveyTitle')}</h2>
                                        <p>{t('OrderFeedback.surveyBody')}</p>
                                    <p className={styles.orderId}>Order ID: {orderFeedbackData?.order_id}</p>
                                </div>

                                <form onSubmit={handleSubmit((data) => onSubmit(data))}>
                                    <div className={styles.surveyV2Progress}>
                                        <div className={styles.surveyV2ProgressHeader}>
                                            {!q1Answer ? (
                                                <>
                                                        <p>{t('OrderFeedback.question1')}</p>
                                                        <p>{t('OrderFeedback.completeAt0')}</p>
                                                </>
                                            ) : (
                                                <>
                                                    <p>{`Question ${Math.max(1, currentIndex + 1)} of ${visibleQuestions.length}`}</p>
                                                    <p>{`${Math.round(((Math.max(1, currentIndex + 1)) / visibleQuestions.length) * 100)}% Complete`}</p>
                                                </>
                                            )}
                                        </div>
                                        <div className={styles.progressBarContainer}>
                                            <div
                                                className={styles.progressBar}
                                                style={{ width: !q1Answer ? '0%' : `${((Math.max(1, currentIndex + 1)) / visibleQuestions.length) * 100}%` }}
                                            />
                                        </div>
                                    </div>

                                    {activeQuestion && (
                                        <div className={styles.questionCard}>
                                            {activeQuestion.input_type === feedbackInputType.Rating && (
                                                <div>
                                                    <p className="fontWeight400">{`${activeQuestion.input_label}`}</p>
                                                            <RatingsV2
                                                                id={`question_${activeQuestion.feedback_question_id}`}
                                                                key={`rate-${activeQuestion.feedback_question_id}`}
                                                                control={control}
                                                            />
                                                </div>
                                            )}

                                            {activeQuestion.input_type === feedbackInputType.YesNo && (
                                                <div>
                                                    <p className="fontWeight400">{`${activeQuestion.input_label}`}</p>
                                                    <YesNoField
                                                        key={`yn-${activeQuestion.feedback_question_id}`}
                                                        question={activeQuestion}
                                                        control={control}
                                                        setValue={setValue}
                                                        orderFeedbackQuestions={orderFeedbackQuestions}
                                                    />
                                                </div>
                                            )}

                                            {activeQuestion.input_type === feedbackInputType.Text && (
                                                <div>
                                                    <p className="fontWeight400">{`${activeQuestion.input_label}`}</p>
                                                    <StableTextArea
                                                        key={`txt-${activeQuestion.feedback_question_id}`}
                                                        name={`question_${activeQuestion.feedback_question_id}`}
                                                        control={control}
                                                        placeholder={activeQuestion.input_placeholder}
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    )}

                                        <div className={styles.questionButton}>
                                        <Button
                                            variant="outlined"
                                            disabled={currentIndex === 0}
                                            onClick={goToPrevious}
                                            className={`secondaryButton ${currentIndex === 0 ? styles.prevButtonDisabled : ''}`}
                                        >
                                            {t('OrderFeedback.previous')}
                                        </Button>

                                          {currentIndex === visibleQuestions.length - 1 && q1Answer ? (
                                            <Button
                                                color='primary'
                                                data-testid="submitButton"
                                                type="button"
                                                onClick={handleFinalSubmit}
                                                disabled={!canGoNext}
                                            >
                                                {t('OrderFeedback.submit')}
                                            </Button>
                                        ) : (
                                            <Button
                                                onClick={goToNext}
                                                disabled={!canGoNext}
                                                color="primary"
                                            >
                                                {t('OrderFeedback.next')}
                                            </Button>
                                        )}
                                    </div>
                                </form>
                            </div>
                        ) : (
                            isLoading && (
                                <div className="marginVerticalThirty">
                                    <NoData message={<img alt="Loading..." src={loader} />} />
                                </div>
                            )
                        )}
                    </Container>
                </div>
            )}
        </>
    );
};

export default withTitle(OrderFeedbackV2, 'Order Feedback');